import { IJob, IUpdateJobPayload } from '~/utils/interface/job';
import { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIn, useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { cast, downloadCSV, handleApiError, notify } from '~/utils/helper';
import { DeleteCircle, PlusIcon, ArrowLeftDoubleIcon, TrashIcon } from '~/assets/icons';
import { handleGetJob } from '~/thunks/job/jobThunk';
import { BaseDatePicker, BaseRow, BaseTimePicker } from '~/components/common';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { adminRouteAbsolute } from '~/utils/constants/route';
import { handleUpdateJob } from '~/thunks/job/jobThunk';
import { handleGetAreaScopeList } from '~/thunks/areaScope/areaScopeThunk';
import ModalAddRoom from './Modal/ModalAddRoom';
import jobApi from '~/apis/job';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { MASTER_DATA_DEHU_EQUIPMENTS } from '~/utils/constants/master';

const initialValues: IUpdateJobPayload = {
  job: {},
};

const AreaPsychorometricReadings = () => {
  const dispatch = useAppDispatch();
  const [job, setJob] = useState<IJob>();
  const { t } = useTranslation('admin/job');
  const { id } = useParams();
  const navigate = useNavigate();
  const modalActionRef = useRef<((roomId: string, roomName: string) => void) | null>(null);
  const { dehuEquipments } = useAppSelector((state) => state.master);
  const [openAddRoom, setOpenAddRoom] = useState<boolean>(false);
  const [modalType, setModalType] = useState<'affected' | 'dh'>('affected');

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await dispatch(
          handleUpdateJob({
            id: Number(job?.id),
            params: { ...values },
          })
        ).unwrap();
        notify('success', t('editSuccess', { ns: 'admin/common' }));
        if (id) {
          navigate(`${adminRouteAbsolute.jobDetail.replace(':id', id)}#areaScope`);
        }
      } catch (e: any) {
        handleApiError(e, setFieldError);
      }
    },
  });

  useEffect(() => {
    if (job) {
      form.setFieldValue('job.psychrometicReadings', cast(job?.psychrometicReadings));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job]);

  useEffect(() => {
    if (job?.id) {
      dispatch(handleGetAreaScopeList({ jobId: job.id }))
        .unwrap()
        .catch(console.error);
    }
  }, [dispatch, job?.id]);

  useEffect(() => {
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_DEHU_EQUIPMENTS],
      })
    ).unwrap();
  }, []);

  const handleSetOpen = () => {
    form.resetForm();
    if (id) {
      navigate(`${adminRouteAbsolute.jobDetail.replace(':id', id)}#areaScope`);
    }
  };

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const job = await dispatch(handleGetJob(id.toString())).unwrap();
          job.jobTypeId = job.jobType.id;
          setJob(job);
        } catch (e) {
          handleApiError(e);
        }
      })();
    }
  }, [id, dispatch]);

  const addNewOutsideItem = () => {
    form.setFieldValue('job.psychrometicReadings.outside', [
      ...(form.values.job.psychrometicReadings?.outside ?? []),
      {
        date: undefined,
        time: undefined,
        temp: undefined,
        rh: undefined,
        gpp: undefined,
      },
    ]);
  };

  const addNewUnaffectedItem = () => {
    form.setFieldValue('job.psychrometicReadings.unaffected', [
      ...(form.values.job.psychrometicReadings?.unaffected ?? []),
      {
        date: undefined,
        time: undefined,
        temp: undefined,
        rh: undefined,
        gpp: undefined,
      },
    ]);
  };

  const addNewHvacItem = () => {
    form.setFieldValue('job.psychrometicReadings.hvac', [
      ...(form.values.job.psychrometicReadings?.hvac ?? []),
      {
        date: undefined,
        time: undefined,
        temp: undefined,
        rh: undefined,
        gpp: undefined,
      },
    ]);
  };

  const handleDeleteOutside = (index: number) => {
    const updatedFields = [...(form.values.job.psychrometicReadings.outside ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('job.psychrometicReadings.outside', updatedFields);
  };

  const handleDeleteUnaffected = (index: number) => {
    const updatedFields = [...(form.values.job.psychrometicReadings.unaffected ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('job.psychrometicReadings.unaffected', updatedFields);
  };

  const handleDeleteHvac = (index: number) => {
    const updatedFields = [...(form.values.job.psychrometicReadings.hvac ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('job.psychrometicReadings.hvac', updatedFields);
  };

  const renderOutsideField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <BaseDatePicker
              placeholder='Select Date'
              value={form.values.job?.psychrometicReadings?.outside[index].date}
              onChange={(date) => {
                if (date) {
                  const selectedDate = dayjs(date).format('MMM DD, YYYY');

                  form.setFieldValue(`job.psychrometicReadings.outside.${index}.date`, selectedDate);
                }
              }}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.outside.${index}.date`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.outside.${index}.date`)}
            />
          </Box>
          <Box className='w-full'>
            <BaseTimePicker
              placeholder='Select Time'
              value={
                form.values.job?.psychrometicReadings?.outside[index].time
                  ? dayjs(form.values.job?.psychrometicReadings?.outside[index].time, 'HH:mm').toDate()
                  : undefined
              }
              onChange={(time) => {
                const startTime = dayjs(time).format('HH:mm');
                form.setFieldValue(`job.psychrometicReadings.outside.${index}.time`, startTime);
              }}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.outside.${index}.time`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.outside.${index}.time`)}
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`job.psychrometicReadings.outside.${index}.temp`}
              placeholder='Temp'
              value={cast(form.values.job?.psychrometicReadings?.outside[index].temp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.outside.${index}.temp`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.outside.${index}.temp`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`job.psychrometicReadings.outside.${index}.rh`}
              placeholder='RH'
              value={cast(form.values.job?.psychrometicReadings?.outside[index].rh)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.outside.${index}.rh`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.outside.${index}.rh`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`job.psychrometicReadings.outside.${index}.gpp`}
              placeholder='GPP'
              value={cast(form.values.job?.psychrometicReadings?.outside[index].gpp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.outside.${index}.gpp`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.outside.${index}.gpp`)}
              fullWidth
            />
          </Box>
          <Box className='w-[5px]'>
            <DeleteCircle
              className='w-[20px] h-[20px] cursor-pointer mt-[8px]'
              onClick={() => handleDeleteOutside(index)}
            />
          </Box>
        </BaseRow>
      </Box>
    );
  };

  const renderUnaffectedField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <BaseDatePicker
              placeholder='Select Date'
              value={form.values.job?.psychrometicReadings?.unaffected[index].date}
              onChange={(date) => {
                if (date) {
                  const selectedDate = dayjs(date).format('MMM DD, YYYY');

                  form.setFieldValue(`job.psychrometicReadings.unaffected.${index}.date`, selectedDate);
                }
              }}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.unaffected.${index}.date`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.unaffected.${index}.date`)}
            />
          </Box>
          <Box className='w-full'>
            <BaseTimePicker
              placeholder='Select Time'
              value={
                form.values.job?.psychrometicReadings?.unaffected[index].time
                  ? dayjs(form.values.job?.psychrometicReadings?.unaffected[index].time, 'HH:mm').toDate()
                  : undefined
              }
              onChange={(time) => {
                const startTime = dayjs(time).format('HH:mm');
                form.setFieldValue(`job.psychrometicReadings.unaffected.${index}.time`, startTime);
              }}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.unaffected.${index}.time`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.unaffected.${index}.time`)}
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`job.psychrometicReadings.unaffected.${index}.temp`}
              placeholder='Temp'
              value={cast(form.values.job?.psychrometicReadings?.unaffected[index].temp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.unaffected.${index}.temp`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.unaffected.${index}.temp`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`job.psychrometicReadings.unaffected.${index}.rh`}
              placeholder='RH'
              value={cast(form.values.job?.psychrometicReadings?.unaffected[index].rh)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.unaffected.${index}.rh`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.unaffected.${index}.rh`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`job.psychrometicReadings.unaffected.${index}.gpp`}
              placeholder='GPP'
              value={cast(form.values.job?.psychrometicReadings?.unaffected[index].gpp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.unaffected.${index}.gpp`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.unaffected.${index}.gpp`)}
              fullWidth
            />
          </Box>
          <Box className='w-[5px]'>
            <DeleteCircle
              className='w-[20px] h-[20px] cursor-pointer mt-[8px]'
              onClick={() => handleDeleteUnaffected(index)}
            />
          </Box>
        </BaseRow>
      </Box>
    );
  };

  const renderHvacField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <BaseDatePicker
              placeholder='Select Date'
              value={form.values.job?.psychrometicReadings?.hvac[index].date}
              onChange={(date) => {
                if (date) {
                  const selectedDate = dayjs(date).format('MMM DD, YYYY');

                  form.setFieldValue(`job.psychrometicReadings.hvac.${index}.date`, selectedDate);
                }
              }}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.hvac.${index}.date`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.hvac.${index}.date`)}
            />
          </Box>
          <Box className='w-full'>
            <BaseTimePicker
              placeholder='Select Time'
              value={
                form.values.job?.psychrometicReadings?.hvac[index].time
                  ? dayjs(form.values.job?.psychrometicReadings?.hvac[index].time, 'HH:mm').toDate()
                  : undefined
              }
              onChange={(time) => {
                const startTime = dayjs(time).format('HH:mm');
                form.setFieldValue(`job.psychrometicReadings.hvac.${index}.time`, startTime);
              }}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.hvac.${index}.time`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.hvac.${index}.time`)}
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`job.psychrometicReadings.hvac.${index}.temp`}
              placeholder='Temp'
              value={cast(form.values.job?.psychrometicReadings?.hvac[index].temp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.hvac.${index}.temp`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.hvac.${index}.temp`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`job.psychrometicReadings.hvac.${index}.rh`}
              placeholder='RH'
              value={cast(form.values.job?.psychrometicReadings?.hvac[index].rh)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.hvac.${index}.rh`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.hvac.${index}.rh`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`job.psychrometicReadings.hvac.${index}.gpp`}
              placeholder='GPP'
              value={cast(form.values.job?.psychrometicReadings?.hvac[index].gpp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `job.psychrometicReadings.hvac.${index}.gpp`))}
              helperText={getIn(form.errors, `job.psychrometicReadings.hvac.${index}.gpp`)}
              fullWidth
            />
          </Box>
          <Box className='w-[5px]'>
            <DeleteCircle
              className='w-[20px] h-[20px] cursor-pointer mt-[8px]'
              onClick={() => handleDeleteHvac(index)}
            />
          </Box>
        </BaseRow>
      </Box>
    );
  };

  const handleDeleteAffectedRoom = (index: number) => {
    const affectedRooms = form.values?.job?.psychrometicReadings?.affected || [];

    if (affectedRooms && index >= 0 && index < affectedRooms.length) {
      const updatedAffectedRooms = affectedRooms.filter((_: any, i: number) => i !== index);

      form.setFieldValue('job.psychrometicReadings.affected', updatedAffectedRooms);
    }
  };

  const handleDeleteDhRoom = (index: number) => {
    const dhRooms = form.values?.job?.psychrometicReadings?.dh || [];

    if (dhRooms && index >= 0 && index < dhRooms.length) {
      const updatedDhRooms = dhRooms.filter((_: any, i: number) => i !== index);

      form.setFieldValue('job.psychrometicReadings.dh', updatedDhRooms);
    }
  };

  const handleDeleteAffected = (idx: number, index: number) => {
    const affectedReadings: any = form.values?.job?.psychrometicReadings?.affected?.[index]?.readings;

    // If affected readings exist and the idx is valid, remove the item at idx
    if (affectedReadings && idx >= 1 && idx < affectedReadings.length) {
      const updatedReadings = affectedReadings.filter((_: any, i: any) => i !== idx);

      form.setFieldValue(`job.psychrometicReadings.affected[${index}].readings`, updatedReadings);
    }
  };

  const addNewAffectedItem = (index: number) => {
    const newAffectedFields = [...form.values.job.psychrometicReadings.affected];
    newAffectedFields[index].readings.push({
      date: '',
      time: '',
      temp: '',
      rh: '',
      gpp: '',
    });
    form.setFieldValue('job.psychrometicReadings.affected', newAffectedFields);
  };

  const handleDeleteDh = (idx: number, index: number) => {
    const dhReadings: any = form.values?.job?.psychrometicReadings?.dh?.[index]?.readings;

    // If dh readings exist and the idx is valid, remove the item at idx
    if (dhReadings && idx >= 1 && idx < dhReadings.length) {
      const updatedReadings = dhReadings.filter((_: any, i: any) => i !== idx);

      form.setFieldValue(`job.psychrometicReadings.dh[${index}].readings`, updatedReadings);
    }
  };

  const addNewDhItem = (index: number) => {
    const newDhFields = [...form.values.job.psychrometicReadings.dh];
    newDhFields[index].readings.push({
      dehu: '',
      date: '',
      time: '',
      temp: '',
      rh: '',
      gpp: '',
    });
    form.setFieldValue('job.psychrometicReadings.dh', newDhFields);
  };

  const handleOpenModal = (
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>,
    setFieldValueCallback: (roomId: string, roomName: string) => void
  ) => {
    setOpenModal(true);
    modalActionRef.current = setFieldValueCallback;
  };

  const handleAddAffected = () => {
    handleOpenModal(setOpenAddRoom, addAffectedField);
    setModalType('affected');
  };

  const addAffectedField = (roomId: string, roomName: string) => {
    const newField = {
      roomId,
      roomName,
      readings: [
        {
          date: undefined,
          time: undefined,
          temp: undefined,
          rh: undefined,
          gpp: undefined,
        },
      ],
    };

    form.setFieldValue('job.psychrometicReadings.affected', [
      ...(form.values.job.psychrometicReadings?.affected ?? []),
      newField,
    ]);
  };

  const handleOpenAddRoom = (value: boolean) => {
    setOpenAddRoom(value);
  };

  const handleAddDh = () => {
    handleOpenModal(setOpenAddRoom, addDhField);
    setModalType('dh');
  };

  const addDhField = (roomId: string, roomName: string) => {
    const newField = {
      roomId,
      roomName,
      readings: [
        {
          date: undefined,
          time: undefined,
          temp: undefined,
          rh: undefined,
          gpp: undefined,
        },
      ],
    };

    form.setFieldValue('job.psychrometicReadings.dh', [...(form.values.job.psychrometicReadings?.dh ?? []), newField]);
  };

  const handleSelectRoom = (roomId: string, roomName: string) => {
    if (modalActionRef.current) {
      modalActionRef.current(roomId, roomName);
    }
  };
  const handleExport = async () => {
    const now = dayjs().format('YYYY-MM-DD');
    const fileName = `${job?.name}_psychrometric_readings_${now}.csv`;
    const res = await jobApi.exportAtmospherics({ jobId: job?.id ?? '' });
    downloadCSV(res.data, fileName);
  };
  return (
    <Box>
      <DialogTitle className='flex flex-row items-center justify-between'>
        <Box>
          <IconButton onClick={handleSetOpen} className='p-0 w-[40px] h-[40px]'>
            <ArrowLeftDoubleIcon className='w-[11px] mb-[5px]' />
          </IconButton>
          {t('editPsychrometricReadings')}
        </Box>
        <Button
          className={'bg-[#10a479] mr-[10px] hover:bg-[#10a479]'}
          variant='contained'
          disableElevation
          onClick={handleExport}
        >
          {t('export')}
        </Button>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <DialogTitle>{t('atmosphericReadings')}</DialogTitle>
        </Grid>
        <Grid container spacing={3}>
          <DialogTitle className='!text-[15px]'>Outside</DialogTitle>
        </Grid>
        {form.values.job.psychrometicReadings?.outside?.length > 0 && (
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Date</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>Time</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>Temp</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>RH</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>GPP</InputLabel>
            </Box>
            <Box className='w-[5px]'> </Box>
          </BaseRow>
        )}
        {form.values.job.psychrometicReadings?.outside?.map((item: any, index: number) =>
          renderOutsideField(item, index)
        )}

        <Grid container spacing={3} className='mb-[24px]'>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}>
            <Button
              onClick={addNewOutsideItem}
              className='whitespace-nowrap'
              startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            >
              Add Item
            </Button>
          </Grid>
        </Grid>
        {/* Unaffected */}
        <Grid container spacing={3}>
          <DialogTitle className='!text-[15px]'>Unaffected</DialogTitle>
        </Grid>
        {form.values.job.psychrometicReadings?.unaffected?.length > 0 && (
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Date</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>Time</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>Temp</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>RH</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>GPP</InputLabel>
            </Box>
            <Box className='w-[5px]'> </Box>
          </BaseRow>
        )}
        {form.values.job.psychrometicReadings?.unaffected?.map((item: any, index: number) =>
          renderUnaffectedField(item, index)
        )}
        <Grid container spacing={3} className='mb-[24px]'>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}>
            <Button
              onClick={addNewUnaffectedItem}
              className='whitespace-nowrap'
              startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            >
              Add Item
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <DialogTitle className='!text-[15px]'>HVAC</DialogTitle>
        </Grid>
        {form.values.job.psychrometicReadings?.hvac?.length > 0 && (
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Date</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>Time</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>Temp</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>RH</InputLabel>
            </Box>
            <Box className='w-full'>
              <InputLabel>GPP</InputLabel>
            </Box>
            <Box className='w-[5px]'> </Box>
          </BaseRow>
        )}
        {form.values.job.psychrometicReadings?.hvac?.map((item: any, index: number) => renderHvacField(item, index))}
        <Grid container spacing={3} className='mb-[24px]'>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}>
            <Button
              onClick={addNewHvacItem}
              className='whitespace-nowrap'
              startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            >
              Add Item
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <DialogTitle className='!text-[15px]'>Affected</DialogTitle>
        </Grid>
        {(form.values.job.psychrometicReadings?.affected || []).map((field: any, index: any) => (
          <Box key={index}>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full flex justify-center'>
                <DialogTitle className='!text-[15px]'>{field?.roomName || 'Unnamed Room'}</DialogTitle>
              </Box>
              <Box className='w-[5px]'>
                <TrashIcon
                  className='w-[20px] h-[20px] cursor-pointer mt-[8px]'
                  onClick={() => handleDeleteAffectedRoom(index)}
                />
              </Box>
            </BaseRow>

            {(field?.readings || []).map((reading: any, idx: any) => {
              const affectedPath = `job.psychrometicReadings.affected[${index}].readings[${idx}]`;

              return (
                <Box key={`${index}-${idx}`}>
                  <BaseRow className='gap-[20px] mb-[20px]'>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Date</InputLabel>}
                      <BaseDatePicker
                        placeholder='Select Date'
                        value={
                          form.values?.job?.psychrometicReadings?.affected?.[index]?.readings?.[idx]?.date
                            ? dayjs(form.values.job.psychrometicReadings.affected[index].readings[idx].date).toDate()
                            : undefined
                        }
                        onChange={(date) => {
                          if (date) {
                            const selectedDate = dayjs(date).format('MMM DD, YYYY');
                            form.setFieldValue(`${affectedPath}.date`, selectedDate);
                          }
                        }}
                        error={Boolean(getIn(form.errors, `${affectedPath}.date`))}
                        helperText={getIn(form.errors, `${affectedPath}.date`)}
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Time</InputLabel>}
                      <BaseTimePicker
                        placeholder='Select Time'
                        value={
                          form.values?.job?.psychrometicReadings?.affected?.[index]?.readings?.[idx]?.time
                            ? dayjs(
                                form.values.job.psychrometicReadings.affected[index].readings[idx].time,
                                'HH:mm'
                              ).toDate()
                            : undefined
                        }
                        onChange={(time) => {
                          const startTime = dayjs(time).format('HH:mm');
                          form.setFieldValue(`${affectedPath}.time`, startTime);
                        }}
                        error={Boolean(getIn(form.errors, `${affectedPath}.time`))}
                        helperText={getIn(form.errors, `${affectedPath}.time`)}
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Temp</InputLabel>}
                      <TextField
                        name={`${affectedPath}.temp`}
                        placeholder='Temp'
                        value={form.values?.job?.psychrometicReadings?.affected?.[index]?.readings?.[idx]?.temp ?? ''}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, `${affectedPath}.temp`))}
                        helperText={getIn(form.errors, `${affectedPath}.temp`)}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>RH</InputLabel>}
                      <TextField
                        name={`${affectedPath}.rh`}
                        placeholder='RH'
                        value={form.values?.job?.psychrometicReadings?.affected?.[index]?.readings?.[idx]?.rh ?? ''}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, `${affectedPath}.rh`))}
                        helperText={getIn(form.errors, `${affectedPath}.rh`)}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>GPP</InputLabel>}
                      <TextField
                        name={`${affectedPath}.gpp`}
                        placeholder='GPP'
                        value={form.values?.job?.psychrometicReadings?.affected?.[index]?.readings?.[idx]?.gpp ?? ''}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, `${affectedPath}.gpp`))}
                        helperText={getIn(form.errors, `${affectedPath}.gpp`)}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-[5px]'>
                      {idx === 0 && <Box className='w-[5px] mt-[30px]'> </Box>}
                      <DeleteCircle
                        className='w-[16px] h-[16px] cursor-pointer mt-[8px]'
                        onClick={() => handleDeleteAffected(idx, index)}
                      />
                    </Box>
                  </BaseRow>
                  {idx === field.readings.length - 1 && (
                    <Grid container spacing={3} className='mb-[24px]'>
                      <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
                      <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
                      <Grid className='flex justify-end' item xs={12} sm={4}>
                        <Button
                          onClick={() => addNewAffectedItem(index)}
                          className='whitespace-nowrap'
                          startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
                        >
                          Add Item
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
        <Grid container spacing={3}>
          <DialogTitle className='!text-[15px]'>DH</DialogTitle>
        </Grid>
        {(form.values.job.psychrometicReadings?.dh || []).map((field: any, index: any) => (
          <Box key={index}>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full flex justify-center'>
                <DialogTitle className='!text-[15px]'>{field?.roomName || 'Unnamed Room'}</DialogTitle>
              </Box>
              <Box className='w-[5px]'>
                <TrashIcon
                  className='w-[20px] h-[20px] cursor-pointer mt-[8px]'
                  onClick={() => handleDeleteDhRoom(index)}
                />
              </Box>
            </BaseRow>

            {(field?.readings || []).map((reading: any, idx: any) => {
              const dhPath = `job.psychrometicReadings.dh[${index}].readings[${idx}]`;

              return (
                <Box key={`${index}-${idx}`}>
                  <BaseRow className='gap-[20px] mb-[20px]'>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Dehumidifier</InputLabel>}
                      <Autocomplete
                        freeSolo
                        options={dehuEquipments?.map((dehuEquipment: any) => dehuEquipment.name) ?? []}
                        value={cast(form.values?.job?.psychrometicReadings?.dh?.[index]?.readings?.[idx]?.dehu)}
                        onChange={(e, value) => {
                          form.setFieldValue(`${dhPath}.dehu`, cast(value));
                        }}
                        onInputChange={(_e, value) => {
                          form.setFieldValue(`${dhPath}.dehu`, cast(value));
                        }}
                        renderInput={(params) => <TextField {...params} placeholder='Dehumidifier' fullWidth />}
                      />
                    </Box>

                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Date</InputLabel>}
                      <BaseDatePicker
                        placeholder='Select Date'
                        value={
                          form.values?.job?.psychrometicReadings?.dh?.[index]?.readings?.[idx]?.date
                            ? dayjs(form.values.job.psychrometicReadings.dh[index].readings[idx].date).toDate()
                            : undefined
                        }
                        onChange={(date) => {
                          if (date) {
                            const selectedDate = dayjs(date).format('MMM DD, YYYY');
                            form.setFieldValue(`${dhPath}.date`, selectedDate);
                          }
                        }}
                        error={Boolean(getIn(form.errors, `${dhPath}.date`))}
                        helperText={getIn(form.errors, `${dhPath}.date`)}
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Time</InputLabel>}
                      <BaseTimePicker
                        placeholder='Select Time'
                        value={
                          form.values?.job?.psychrometicReadings?.dh?.[index]?.readings?.[idx]?.time
                            ? dayjs(form.values.job.psychrometicReadings.dh[index].readings[idx].time, 'HH:mm').toDate()
                            : undefined
                        }
                        onChange={(time) => {
                          const startTime = dayjs(time).format('HH:mm');
                          form.setFieldValue(`${dhPath}.time`, startTime);
                        }}
                        error={Boolean(getIn(form.errors, `${dhPath}.time`))}
                        helperText={getIn(form.errors, `${dhPath}.time`)}
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Temp</InputLabel>}
                      <TextField
                        name={`${dhPath}.temp`}
                        placeholder='Temp'
                        value={form.values?.job?.psychrometicReadings?.dh?.[index]?.readings?.[idx]?.temp ?? ''}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, `${dhPath}.temp`))}
                        helperText={getIn(form.errors, `${dhPath}.temp`)}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>RH</InputLabel>}
                      <TextField
                        name={`${dhPath}.rh`}
                        placeholder='RH'
                        value={form.values?.job?.psychrometicReadings?.dh?.[index]?.readings?.[idx]?.rh ?? ''}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, `${dhPath}.rh`))}
                        helperText={getIn(form.errors, `${dhPath}.rh`)}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>GPP</InputLabel>}
                      <TextField
                        name={`${dhPath}.gpp`}
                        placeholder='GPP'
                        value={form.values?.job?.psychrometicReadings?.dh?.[index]?.readings?.[idx]?.gpp ?? ''}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, `${dhPath}.gpp`))}
                        helperText={getIn(form.errors, `${dhPath}.gpp`)}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-[5px]'>
                      {idx === 0 && <Box className='w-[5px] mt-[30px]'> </Box>}
                      <DeleteCircle
                        className='w-[16px] h-[16px] cursor-pointer mt-[8px]'
                        onClick={() => handleDeleteDh(idx, index)}
                      />
                    </Box>
                  </BaseRow>
                  {idx === field.readings.length - 1 && (
                    <Grid container spacing={3} className='mb-[24px]'>
                      <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
                      <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
                      <Grid className='flex justify-end' item xs={12} sm={4}>
                        <Button
                          onClick={() => addNewDhItem(index)}
                          className='whitespace-nowrap'
                          startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
                        >
                          Add Item
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
        <BaseRow className='flex justify-center gap-[20px] mb-[20px]'>
          <Box>
            <Button color='primary' variant='contained' onClick={() => handleAddAffected()}>
              Add Affected
            </Button>
          </Box>
          <Box>
            <Button color='primary' variant='contained' onClick={() => handleAddDh()}>
              Add DH
            </Button>
          </Box>
        </BaseRow>

        <ModalAddRoom
          open={openAddRoom}
          setOpen={handleOpenAddRoom}
          addAffectedField={handleSelectRoom}
          currentScreen='psychrometicReading'
          type={modalType}
        />
      </DialogContent>
      <DialogActions>
        <Button color='secondary' variant='contained' onClick={() => handleSetOpen()}>
          {t('trackTime.cancel')}
        </Button>
        <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
          {t('trackTime.save')}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default AreaPsychorometricReadings;
